import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Download,
  ExternalLink,
  Share2,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Cpu,
  Layers,
  Globe2,
  Copy,
  Check
} from "lucide-react";
import { CtaBanner } from "../components/Sections";
import { useToast } from "../hooks/use-toast";

export default function CompanyProfilePage() {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const pdfUrl = "/company-profile.pdf";
  const downloadFilename = "BUGz_X_Company_Profile.pdf";

  const handleCopyLink = () => {
    const fullUrl = `${window.location.origin}${pdfUrl}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      if (toast) {
        toast({
          title: "Link Copied!",
          description: "Direct Company Profile PDF URL copied to clipboard.",
        });
      }
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const KEY_HIGHLIGHTS = [
    {
      icon: Cpu,
      title: "AI-Powered Automation",
      desc: "Tailored AI assistants, automated pipeline workflows, intelligent lead triage, and custom enterprise LLM integrations."
    },
    {
      icon: Layers,
      title: "Enterprise Custom Software & ERP",
      desc: "High-performance full-stack web applications, mobile platforms (iOS/Android), ERP systems, and cloud architectures."
    },
    {
      icon: Globe2,
      title: "Performance & Growth Engine",
      desc: "Multi-channel acquisition strategies across Google, Meta, and LinkedIn with CRM automation that scales sales clarity."
    },
    {
      icon: ShieldCheck,
      title: "Security & Strategic Delivery",
      desc: "Rigorous code quality, data compliance (DPDP/GDPR), zero-trust infrastructure, and dedicated milestone governance."
    }
  ];

  return (
    <main className="pt-32 md:pt-40">
      {/* Hero Section */}
      <section className="relative max-w-6xl mx-auto px-4 md:px-6 pb-12">
        <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] font-semibold text-black/70">
          <span className="h-px w-8 bg-black" />
          OFFICIAL CAPABILITIES DECK
        </div>

        <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <h1 className="font-orbitron font-black text-4xl sm:text-5xl md:text-6xl leading-[1.0] tracking-tight">
              BUGz X COMPANY PROFILE
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-black/65 leading-relaxed">
              Explore our comprehensive capabilities document detailing our engineering stack, AI growth systems,
              operating framework, past executions, and long-term partnership models.
            </p>
          </div>

          {/* Quick File Meta Badge */}
          <div className="inline-flex items-center gap-3 self-start lg:self-end rounded-2xl border border-black/15 bg-black/5 px-4 py-3 text-xs">
            <FileText className="h-5 w-5 text-black" />
            <div>
              <p className="font-bold text-black font-orbitron">PDF Edition 2026</p>
              <p className="text-black/60 text-[11px]">Size: ~3.98 MB • Print &amp; Web Ready</p>
            </div>
          </div>
        </div>

        {/* Action Buttons Toolbar */}
        <div className="mt-8 flex flex-wrap items-center gap-3 pt-6 border-t border-black/10">
          <a
            href={pdfUrl}
            download={downloadFilename}
            className="group inline-flex items-center gap-2 rounded-full bg-black text-white px-6 py-3.5 text-xs sm:text-sm font-bold tracking-[0.12em] hover:bg-black/85 transition-all shadow-sm"
          >
            <Download className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            DOWNLOAD COMPANY PROFILE (PDF)
          </a>

          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full border border-black/20 bg-white px-5 py-3.5 text-xs sm:text-sm font-bold tracking-[0.12em] hover:border-black transition-all"
          >
            <ExternalLink className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            OPEN IN NEW TAB
          </a>

          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex items-center gap-2 rounded-full border border-black/20 bg-white px-4 py-3.5 text-xs sm:text-sm font-semibold tracking-wider hover:border-black transition-all"
            aria-label="Copy Direct PDF Link"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-black/70" />}
            <span>{copied ? "LINK COPIED!" : "COPY PDF LINK"}</span>
          </button>
        </div>
      </section>

      {/* Embedded PDF Viewer Section */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-16">
        <div className="rounded-3xl border border-black/15 bg-[#fcfcfc] p-3 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between px-2 pb-3 border-b border-black/10 text-xs text-black/60">
            <span className="flex items-center gap-2 font-mono font-medium">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              Interactive Document Viewer
            </span>
            <div className="flex items-center gap-3">
              <a
                href={pdfUrl}
                download={downloadFilename}
                className="hover:text-black font-semibold underline underline-offset-2"
              >
                Save Copy
              </a>
              <span>•</span>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-black font-semibold underline underline-offset-2 inline-flex items-center gap-1"
              >
                Fullscreen <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          <div className="mt-3 relative w-full h-[650px] sm:h-[780px] lg:h-[900px] rounded-2xl overflow-hidden bg-neutral-900/5 border border-black/10">
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=0`}
              title="BUGZ X Official Company Profile PDF"
              className="w-full h-full border-0"
            />
          </div>

          {/* Mobile / Fallback banner */}
          <div className="mt-4 rounded-xl border border-black/10 bg-white p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2.5 text-black/70">
              <Sparkles className="h-4 w-4 text-black shrink-0" />
              <span>
                Tip: On mobile devices or smaller screens, opening the PDF directly in a new tab provides the smoothest reading experience.
              </span>
            </div>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 px-4 py-2 rounded-lg bg-black text-white font-bold tracking-wider hover:bg-black/85"
            >
              Open Fullscreen
            </a>
          </div>
        </div>
      </section>

      {/* Profile Overview & Value Pillars */}
      <section className="border-t border-black/10 bg-[#fafafa] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] font-semibold">
            <span className="h-px w-8 bg-black" />
            WHAT YOU’LL FIND IN THIS DECK
          </div>
          <h2 className="mt-4 font-orbitron font-black text-3xl md:text-4xl leading-tight max-w-3xl">
            A comprehensive look at our <span className="italic font-normal text-black/60">methodology and reach</span>
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {KEY_HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <div key={i} className="rounded-2xl border border-black/12 bg-white p-7 hover:border-black/40 transition-all shadow-sm">
                  <div className="h-10 w-10 rounded-xl bg-black/5 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-black" />
                  </div>
                  <h3 className="mt-5 font-orbitron font-bold text-lg">{h.title}</h3>
                  <p className="mt-2.5 text-sm text-black/65 leading-relaxed">{h.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Quick Checklist */}
          <div className="mt-10 rounded-2xl border border-black/12 bg-white p-6 sm:p-8">
            <p className="text-[11px] tracking-[0.25em] font-bold text-black/50 uppercase">
              Deck Highlights &amp; Inclusions
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs sm:text-sm text-black/75">
              {[
                "Executive Summary & Vision",
                "End-to-End Service Portfolio",
                "Proven Growth & Retention Systems",
                "AI Agent Architecture & Automation",
                "Client Delivery Roadmaps (30/60/90)",
                "Transparent Engagement Models",
                "Enterprise Security & Standards",
                "Leadership & Technical Team",
                "Global Client Reach & Proof Points"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-black shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <CtaBanner
        title="Ready to discuss how BUGZ X can scale your systems?"
        text="Whether you need automated growth pipelines, high-performance web engineering, or dedicated AI implementation, our team is ready to map your plan."
        cta="SCHEDULE CONSULTATION"
        to="/contact"
      />
    </main>
  );
}
